import { FC } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";

import CardDailyPrompt from "../CardDailyPrompt/CardDailyPrompt";
import {
  selectByDatePromptsSortedByDailyAverageScoresState,
  selectLoading as selectPromptsListLoading,
  selectPromptsState,
} from "../../store/selectors/prompts";
import {
  selectDailyAveragePromptLoadingState,
  selectDailyAveragePromptError,
} from "../../store/selectors/scores";
import CardDailyPromptSkeleton from "../CardDailyPrompt/CardDailyPromptSkeleton";
import DataLoadError from "../DataLoadError";
import { toPositiveInt } from "../../utils/toPositiveInt";
import { useAppDispatch } from "../../hooks";
import { fetchBrandByPromptsSeries } from "../../store/scores/slice";

const DEFAULT_ERROR_MESSAGE = "Failed to load data";

const CardDailyPromptWrapper: FC = () => {
  const { id, date } = useParams();
  const dispatch = useAppDispatch();
  const safeBrandId = toPositiveInt(id) ?? -1;
  const prompts = useSelector(selectByDatePromptsSortedByDailyAverageScoresState(safeBrandId, date));
  const loading = useSelector(selectDailyAveragePromptLoadingState);
  const error = useSelector(selectDailyAveragePromptError);
  const promptsItems = useSelector(selectPromptsState);
  const promptsListLoading = useSelector(selectPromptsListLoading);

  const hasRouteParams = safeBrandId > 0 && Boolean(date);
  const listStillLoading = promptsItems === null || promptsListLoading;
  const scoresNotReady = loading === "idle" || loading === "loading";
  const derivedMissing = prompts == null;

  if (loading === "error") {
    return (
      <DataLoadError
        message={error ?? DEFAULT_ERROR_MESSAGE}
        onRetry={() => dispatch(fetchBrandByPromptsSeries({ brand_id: safeBrandId, days: 16 }))}
      />
    );
  }

  if (
    hasRouteParams &&
    derivedMissing &&
    (scoresNotReady || (loading === "success" && listStillLoading))
  ) {
    return <CardDailyPromptSkeleton />;
  }

  if (prompts == null || prompts.length === 0) return null;

  return (
    <>
      {prompts.map((prompt, index) => (
        <CardDailyPrompt key={prompt.id} prompt={prompt} index={index} />
      ))}
    </>
  );
};

export default CardDailyPromptWrapper;